/**
 * Parse an API error response body into a human-readable string.
 *
 * Shared by the admin page and the scheduler dialog — both put DRF error bodies in front of a
 * person, and a dialog that prints raw JSON at them is a bug, not an error message.
 *
 * Handles: { detail }, { message }, { non_field_errors }, { errors: string },
 *          { errors: { field: [msgs] } }, bare { field: [msgs] }, and raw strings.
 */
export function parseApiError(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err.detail) return err.detail;
  if (err.message) return err.message;
  if (err.non_field_errors) return [].concat(err.non_field_errors).join(' ');
  if (err.errors) {
    if (typeof err.errors === 'string') return err.errors;
    if (typeof err.errors === 'object') {
      return Object.entries(err.errors)
        .map(([field, msgs]) => {
          const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
          const msg = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
          return `${fieldLabel}: ${msg}`;
        })
        .join(' · ');
    }
  }
  // DRF field errors arrive unwrapped too: { phone: ["Include the country code."] }.
  // Label each field so the message reads as a sentence rather than raw JSON.
  try {
    const parts = Object.entries(err)
      .map(([field, msgs]) => {
        const flat = [].concat(msgs).filter((m) => typeof m === 'string');
        if (!flat.length) return null;
        const label = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
        return field === 'non_field_errors' ? flat.join(', ') : `${label}: ${flat.join(', ')}`;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' · ');
  } catch (_) { /* ignore */ }
  return fallback;
}
